// Nestly — seed content (all Italian, original)
// Immagini reali Unsplash — pronte in produzione

export const BRAND = {
  name: "Nestly",
  tagline: "Spazi piccoli, soluzioni chiare.",
  description:
    "Guide pratiche e prodotti selezionati per organizzare piccoli appartamenti italiani in 15 minuti al giorno.",
  affiliateDisclosure:
    "Alcuni link sono commerciali: potremmo ricevere una commissione o vendere direttamente. Il prezzo per te non cambia.",
};

export const MARQUEE_PHRASES = [
  "organizzazione cucina",
  "dispensa ordinata",
  "armadio piccolo",
  "bajo lavello",
  "scrivania WFH",
  "bagno hotel look",
  "under €30",
  "routine 15 minuti",
  "contenitori trasparenti",
  "ingresso ordinato",
  "lazy susan",
  "cable management",
];

export const CATEGORIES = [
  {
    slug: "cucina-dispensa",
    name: "Cucina & dispensa",
    subtitle: "01",
    blurb: "Piani liberi, dispensa leggibile, cotture veloci.",
    image:
      "https://images.unsplash.com/photo-1631048835473-73c7aaf86096?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
  {
    slug: "armadio-piccolo",
    name: "Armadio piccolo",
    subtitle: "02",
    blurb: "Meno pieghe, più spazio, sistema per stagioni.",
    image:
      "https://images.unsplash.com/photo-1687953413905-731f620177ae?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
  {
    slug: "bagno",
    name: "Bagno",
    subtitle: "03",
    blurb: "Look da hotel, anche in 4 metri quadri.",
    image:
      "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
  {
    slug: "scrivania-wfh",
    name: "Scrivania WFH",
    subtitle: "04",
    blurb: "Postazione ordinata per lavorare da casa senza rumore visivo.",
    image:
      "https://images.unsplash.com/photo-1593062096033-9a26b09da705?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
  {
    slug: "ingresso-ripostiglio",
    name: "Ingresso & ripostiglio",
    subtitle: "05",
    blurb: "Il micro-angolo che decide la giornata.",
    image:
      "https://images.unsplash.com/photo-1721739508038-a1e112255e52?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
  {
    slug: "under-30",
    name: "Under €30",
    subtitle: "06",
    blurb: "Piccoli acquisti che cambiano l'ordine di una stanza.",
    image:
      "https://images.unsplash.com/photo-1776219189350-728ddf739d6d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  },
];

export const MANIFESTO = [
  {
    n: "01",
    title: "Un cassetto per volta",
    body: "Non si riordina una casa in un giorno. Si riordina un cassetto, un ripiano, un angolo. La coerenza vince sull'entusiasmo.",
  },
  {
    n: "02",
    title: "Meno oggetti, più spazio",
    body: "Prima di comprare organizer, si tolgono le cose che non usiamo da un anno. È gratis, ed è la mossa che libera più metri.",
  },
  {
    n: "03",
    title: "Ogni cosa ha un posto",
    body: "L'ordine non è estetico, è funzionale. Se il posto è comodo, la casa resta in ordine da sola.",
  },
  {
    n: "04",
    title: "Trasparenza",
    body: "Contenitori chiari, etichette semplici. Quello che si vede si usa. Quello che non si vede si dimentica.",
  },
  {
    n: "05",
    title: "Quindici minuti al giorno",
    body: "Non un'ora nel weekend. Quindici minuti alla sera, con una piccola playlist. È tutto quello che serve.",
  },
];

/**
 * Editorial metadata Nestly does not have yet. Left null on purpose: never invent
 * a publication date or an author.
 *
 * Filling `publishedAt` with a real date (ISO or an Italian long date such as
 * "12 dicembre 2025") is what unlocks `Article` JSON-LD and the
 * `article:published_time` meta on detail pages — see `src/lib/schema.js`.
 * Until then those pages emit `WebPage`.
 */
const EDITORIAL_DEFAULTS = {
  publishedAt: null,
  updatedAt: null,
  author: null,
};

function withEditorialDefaults(article) {
  return { ...EDITORIAL_DEFAULTS, ...article };
}

export const GUIDES = [
  {
    slug: "dispensa-organizzata-10-minuti",
    category: "cucina-dispensa",
    title: "Dispensa organizzata in 10 minuti",
    excerpt:
      "Il metodo per svuotare, categorizzare e travasare senza spendere ore. Con contenitori trasparenti e un'etichetta semplice.",
    read: "6 min",
    image:
      "https://images.unsplash.com/photo-1592178036182-5400889dfc74?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    meta: {
      title: "Dispensa organizzata in 10 minuti — Guida Nestly",
      description:
        "Come organizzare la dispensa di un appartamento italiano piccolo in 10 minuti: contenitori trasparenti, etichette e regola del primo-piano.",
    },
    intro:
      "La dispensa è l'angolo di casa che si disordina più in fretta e che, se sistemato bene, cambia davvero la percezione della cucina. In dieci minuti, senza svuotare tutto il mobile, si può ottenere un risultato che dura settimane. Non serve rifare l'IKEA, serve una regola semplice: quello che vedi lo usi, quello che non vedi scade. Ecco come fare.",
    sections: [
      {
        h: "Passo 1 — Svuota un ripiano alla volta",
        p: "Non svuotare tutta la dispensa. Prendi un solo ripiano e mettilo sul piano cucina. Butta ciò che è scaduto senza rimpianti (i legumi secchi durano meno di quanto pensi). Raggruppa quello che resta in tre pile: colazione, cottura, snack.",
      },
      {
        h: "Passo 2 — Travasa in contenitori trasparenti",
        p: "La pasta, il riso, i cereali e la farina finiscono in contenitori trasparenti con chiusura ermetica. Non serve un set costoso: bastano quattro-sei contenitori quadrati che si impilano bene. Il vantaggio è duplice: vedi subito quando finisce qualcosa e non porti in casa scarafaggi della dispensa.",
      },
      {
        h: "Passo 3 — Etichette leggibili, non estetiche",
        p: "Un'etichetta scritta a mano è più veloce e più duratura di una stampata male. Nome del prodotto e data di travaso. Basta.",
      },
      {
        h: "Passo 4 — Regola del primo piano",
        p: "Sul ripiano più accessibile va quello che usi ogni giorno: caffè, olio, pasta corta, sale. Sopra o sotto va tutto il resto. Suona banale, ma la maggior parte delle dispense italiane è organizzata al contrario.",
      },
    ],
    faq: [
      {
        q: "Serve buttare via i sacchetti originali?",
        a: "Solo per pasta, riso, cereali e legumi che tendono a rompersi. Le scatolette e i barattoli restano dove sono.",
      },
      {
        q: "Che contenitori trasparenti sono davvero utili?",
        a: "Quadrati o rettangolari, meglio se impilabili. Rotondi sprecano spazio negli angoli. Cerca capacità 1–2 litri per pasta e riso.",
      },
      {
        q: "Ogni quanto rifare la dispensa?",
        a: "Un piccolo controllo mensile di 5 minuti basta. Il grosso lavoro non si ripete più.",
      },
    ],
  },
  {
    slug: "armadio-stretto-guida",
    category: "armadio-piccolo",
    title: "Armadio stretto: la guida completa",
    excerpt:
      "Divisori verticali, appendiabiti sottili, scatole basse. Come guadagnare il 30% di spazio in un armadio da 80 cm.",
    read: "8 min",
    image:
      "https://images.unsplash.com/photo-1558997519-83ea9252edf8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    meta: {
      title: "Armadio stretto: guida per guadagnare spazio | Nestly",
      description:
        "Come organizzare un armadio stretto in un appartamento italiano: divisori, appendiabiti sottili, piegatura verticale.",
    },
    intro:
      "Se hai un armadio da 80 centimetri e vestiti per due stagioni, non serve un guardaroba nuovo. Servono tre modifiche: appendiabiti tutti uguali e sottili, un ripiano intermedio dove non c'è, e la piegatura verticale per le magliette. Con queste tre cose si guadagna facilmente il 25–30% di spazio, senza spendere più di quaranta euro.",
    sections: [
      {
        h: "1. Appendiabiti tutti uguali",
        p: "Il primo cambiamento è cromatico e dimensionale: appendiabiti tutti dello stesso colore e dello stesso spessore. Quelli in velluto sottile occupano metà spazio degli appendiabiti in plastica e non fanno scivolare i vestiti. Cambio totale in cinque minuti.",
      },
      {
        h: "2. Ripiano intermedio dove serve",
        p: "Nella parte alta o bassa dell'armadio spesso c'è mezzo metro di aria. Un ripiano aggiuntivo, anche appoggiato, raddoppia lo spazio utile per maglioni, jeans e borse.",
      },
      {
        h: "3. Piegatura verticale (KonMari)",
        p: "Le magliette e le felpe leggere si piegano a rettangolo e si mettono in verticale, come schedari. Vedi tutto quello che hai, non hai più la pila che crolla, non stiri più due volte le stesse cose.",
      },
      {
        h: "4. Divisori per calzini e intimo",
        p: "Nei cassetti, dividere è tutto. Bastano cinque-sei riquadri in tessuto per fermare il caos permanente dei cassetti piccoli.",
      },
    ],
    faq: [
      {
        q: "Cambio stagione: valigie o scatole?",
        a: "Scatole basse sotto il letto sono la soluzione più italiana. Traspirano, non si vedono, si aprono facilmente.",
      },
      {
        q: "Quanti appendiabiti prendere?",
        a: "Conta i capi appesi e aggiungi 5. Non di più. Se compri troppi appendiabiti tendi a riempirli.",
      },
      {
        q: "Serve svuotare tutto per iniziare?",
        a: "No. Prova prima la piegatura verticale in un solo cassetto. Se ti convince, estendi.",
      },
    ],
  },
  {
    slug: "bagno-hotel-look",
    category: "bagno",
    title: "Bagno con effetto hotel",
    excerpt:
      "Come far sembrare curato un bagno da 4mq: contenitori simili, asciugamani a rotolo, luce calda.",
    read: "5 min",
    image:
      "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    meta: {
      title: "Bagno con effetto hotel: guida rapida | Nestly",
      description:
        "Il metodo Nestly per un bagno piccolo che sembra un hotel: contenitori coordinati, asciugamani a rotolo, luce calda.",
    },
    intro:
      "L'effetto hotel non dipende dal marmo alle pareti. Dipende da tre cose: tutto il visibile è dello stesso colore o della stessa forma, la luce non è bianca fredda, e sul lavandino non si vedono più di tre oggetti. Basta questo per ribaltare la percezione di un bagno da quattro metri quadri.",
    sections: [
      {
        h: "Regola del tre",
        p: "Sul lavandino resta solo l'essenziale: dispenser sapone, portaspazzolino, un contenitore per il resto. Tutto il resto va nei cassetti. La superficie libera è il segnale visivo più forte di ordine.",
      },
      {
        h: "Contenitori coordinati",
        p: "Cotton fioc, cotone, elastici, campioncini: tutto in tre-quattro contenitori identici, meglio se in vetro smerigliato o ceramica bianca. È una spesa minima con enorme resa estetica.",
      },
      {
        h: "Asciugamani a rotolo",
        p: "Piegati a rotolo e messi in un cestino aperto. Cambia completamente la percezione della stanza rispetto agli asciugamani piegati sul termosifone.",
      },
      {
        h: "Luce calda",
        p: "Sostituisci le lampadine bianche fredde (6000K) con luce calda (2700–3000K). È il singolo cambiamento più efficace e costa dieci euro.",
      },
    ],
    faq: [
      {
        q: "E se non ho spazio nei mobili?",
        a: "Un pensile stretto sopra il WC risolve nella maggior parte dei bagni. Cerca modelli da 30 cm di profondità.",
      },
      {
        q: "Che colori scegliere?",
        a: "Meglio pochi colori, dello stesso tono. Bianco caldo, sabbia, salvia. Niente stampe.",
      },
      {
        q: "Come tenere il bagno in ordine nel tempo?",
        a: "Un contenitore 'da svuotare' vicino alla porta. Ogni sera ci finisce quello che non deve restare in bagno.",
      },
    ],
  },
  {
    slug: "scrivania-pulita-wfh",
    category: "scrivania-wfh",
    title: "Scrivania pulita per lavorare da casa",
    excerpt:
      "Cavi nascosti, cassetto delle piccole cose, luce laterale. Come rendere una scrivania piccola un posto in cui tornare.",
    read: "6 min",
    image:
      "https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    meta: {
      title: "Scrivania pulita: guida WFH italiana | Nestly",
      description:
        "Come organizzare una scrivania piccola per lavorare da casa senza rumore visivo: gestione cavi, cassetti, luce laterale.",
    },
    intro:
      "Lavorare da casa significa passare otto ore davanti a un tavolo. Se quel tavolo è disordinato, ogni piccola distrazione visiva ti costa energia. Non serve un ufficio Pinterest: serve una scrivania funzionale con tre principi. Cavi invisibili, una sola superficie libera, luce che viene dal lato giusto.",
    sections: [
      {
        h: "Cavi giù dalla scrivania",
        p: "Una canalina sotto il piano, un fermacavi adesivo per la ciabatta, un fermacavi a J per il caricatore. Non è ossessione da nerd: è la differenza tra guardare la scrivania con calma o con fastidio.",
      },
      {
        h: "Il cassetto delle piccole cose",
        p: "Penne, chiavetta, post-it, forbici: tutto in un cassetto diviso con vaschette. Sulla scrivania resta lo schermo, la tastiera, il mouse, un blocco note. Basta.",
      },
      {
        h: "Luce da sinistra (o da destra)",
        p: "La luce frontale abbaglia lo schermo. La luce dietro fa contrasto. La luce laterale è quella giusta: una piccola lampada calda, opposta alla mano che scrive.",
      },
      {
        h: "Rituale di chiusura",
        p: "Alla fine della giornata, due minuti di riordino. Tazza in cucina, taccuino chiuso, sedia sotto la scrivania. Domani è tutto pronto.",
      },
    ],
    faq: [
      {
        q: "Meglio scrivania con o senza cassetti?",
        a: "Con cassetti se non hai altro spazio vicino. Se hai un cassettone accanto, va bene anche un piano nudo.",
      },
      {
        q: "Che lampada scegliere?",
        a: "Braccio orientabile, lampadina calda 2700K, base piccola. Tra i 30 e i 60 euro trovi ottime opzioni.",
      },
      {
        q: "Serve un supporto per il portatile?",
        a: "Se lavori più di quattro ore al giorno, sì. Portatile all'altezza degli occhi, tastiera e mouse esterni. La schiena ringrazia dopo due settimane.",
      },
    ],
  },
  {
    slug: "ingresso-trasformato",
    category: "ingresso-ripostiglio",
    title: "Ingresso trasformato in 30 minuti",
    excerpt:
      "Il micro-angolo che decide come inizi e finisci la giornata. Chiavi, borse, scarpe: dove metterli davvero.",
    read: "5 min",
    image:
      "https://images.unsplash.com/photo-1721739508038-a1e112255e52?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    meta: {
      title: "Ingresso trasformato: guida rapida | Nestly",
      description:
        "Il metodo per trasformare un ingresso piccolo in 30 minuti: appendini, ciotola chiavi, tappeto giusto.",
    },
    intro:
      "L'ingresso di un appartamento italiano di solito è un metro quadro. È anche il primo e l'ultimo posto che vedi ogni giorno. Se qui c'è disordine, il resto della casa parte già stanco. La buona notizia è che si sistema in mezz'ora, con quattro elementi.",
    sections: [
      {
        h: "Ganci alti sopra la porta o accanto",
        p: "Due o tre ganci in linea. Uno per la borsa, uno per il cappotto della giornata, uno libero. Se ce ne sono cinque, li riempirai tutti e non uscirai mai con l'ingresso pulito.",
      },
      {
        h: "Ciotola per chiavi e piccole cose",
        p: "Una sola ciotola, poco profonda. Chiavi, portafoglio, chiavette. Se metti anche il caricatore, apri il cassetto e mettilo lì.",
      },
      {
        h: "Scarpiera bassa e coperta",
        p: "Una panca-scarpiera cambia tutto. Le scarpe sparite dalla vista, un posto dove sederti a mettere gli stivali. Se lo spazio è davvero piccolo, un tappeto per le scarpe bagnate.",
      },
      {
        h: "Uno specchio",
        p: "Specchio verticale accanto alla porta. Raddoppia la luce, controlla l'outfit prima di uscire, allarga visivamente lo spazio.",
      },
    ],
    faq: [
      {
        q: "Non ho spazio nemmeno per una panca. Che faccio?",
        a: "Ganci alti + una scarpiera slim da 20 cm di profondità. Ne trovi anche a specchio.",
      },
      {
        q: "Come tenere pulito il pavimento?",
        a: "Un tappeto lavabile in lavatrice. Ogni due settimane in lavatrice a freddo.",
      },
      {
        q: "Meglio ingresso chiaro o scuro?",
        a: "Chiaro, quasi sempre. In un metro quadro il colore chiaro rende lo spazio meno stretto.",
      },
    ],
  },
  {
    slug: "routine-settimanale-15-minuti",
    category: "cucina-dispensa",
    title: "Routine settimanale in 15 minuti",
    excerpt:
      "Il piano leggero per non dover mai più 'rifare' l'ordine. Un giorno, una zona, quindici minuti.",
    read: "7 min",
    image:
      "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    meta: {
      title: "Routine settimanale 15 minuti | Nestly",
      description:
        "Il piano Nestly per tenere la casa in ordine con 15 minuti al giorno, un giorno una zona.",
    },
    intro:
      "Il metodo Nestly non è una grande pulizia mensile. È l'opposto: un piccolo appuntamento giornaliero, non negoziabile, che elimina la necessità di grandi sistemazioni. Quindici minuti, sempre alla stessa ora, con una piccola playlist. Ecco il piano che consigliamo alle lettrici da quasi due anni.",
    sections: [
      {
        h: "Lunedì — Cucina",
        p: "Piani liberi, frigo veloce (butta ciò che non mangerai), lavello asciutto. Non serve pulire a fondo: serve resettare lo sguardo.",
      },
      {
        h: "Martedì — Bagno",
        p: "Superfici, specchio, un asciugamano pulito. Cinque minuti in più, sei felice per tutta la settimana.",
      },
      {
        h: "Mercoledì — Camera",
        p: "Letto rifatto (già fatto la mattina), comodini svuotati, un giro di panno sui piani. Il vestito appeso, non sulla sedia.",
      },
      {
        h: "Giovedì — Ingresso e soggiorno",
        p: "La ciotola svuotata, i cuscini messi a posto, il tappeto scosso. Zero polvere sulle mensole visibili.",
      },
      {
        h: "Venerdì — Scrivania e piccoli angoli",
        p: "Cassetto delle piccole cose, cavi. Piccoli angoli che accumulano più di quanto pensi.",
      },
      {
        h: "Sabato — Un cassetto",
        p: "Uno solo. Il cassetto più fastidioso della settimana. In sei mesi tutta la casa passa in rassegna.",
      },
      {
        h: "Domenica — Riposo",
        p: "Zero riordino. La domenica si vive la casa, non la si sistema.",
      },
    ],
    faq: [
      {
        q: "E se salto un giorno?",
        a: "Non recuperi. Il giorno dopo riprendi con la zona di quel giorno. Il segreto è non accumulare colpe.",
      },
      {
        q: "Funziona anche in coppia?",
        a: "Meglio. Alternate le zone o dividete la stessa zona in due. Quindici minuti diventano otto.",
      },
      {
        q: "Serve la playlist?",
        a: "Aiuta. Il cervello associa la musica al gesto, e dopo tre settimane inizi automaticamente.",
      },
    ],
  },
].map(withEditorialDefaults);

// "Best of" lists — placeholder link e data-sku
export const BEST_OF = [
  {
    slug: "migliori-organizer-sotto-lavello",
    category: "cucina-dispensa",
    title: "I migliori organizer sotto lavello",
    excerpt:
      "L'angolo più difficile della cucina: tubature, dislivelli, buio. Ecco cosa funziona davvero, con criteri di selezione 4★+, spedizione veloce e budget €25–120.",
    read: "9 min",
    image:
      "https://images.unsplash.com/photo-1600489000022-c2086d79f9d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    meta: {
      title: "Migliori organizer sotto lavello 2025 | Nestly",
      description:
        "Selezione Nestly dei migliori organizer sotto lavello: mensole telescopiche, cassetti estraibili, contenitori impilabili. Selezione curata.",
    },
    intro:
      "Il mobile sotto il lavello è l'angolo peggiore della cucina italiana: tubature che rubano spazio, sifone al centro, poca luce, umidità. Un ripiano fisso qui non basta. Servono soluzioni pensate per il caos verticale. Abbiamo selezionato quattro organizer che funzionano davvero, selezionati con più di 800 recensioni ciascuno.",
    criteria: [
      "Materiali resistenti all'umidità (metallo verniciato, PP alimentare)",
      "Recensioni 4 stelle o più, minimo 500 recensioni",
      "Spedizione veloce disponibile",
      "Prezzo tra €25 e €120",
      "Adattabile a spazi con tubature centrali",
    ],
    products: [
      {
        name: "Mensola telescopica a 2 piani",
        badge: "Migliore in generale",
        price: "€34–45",
        pros: "Regolabile in larghezza (aggira il sifone), monta senza viti, portata 15 kg per ripiano.",
        cons: "Non adatta se il mobile è più stretto di 45 cm.",
        sku: "REPLACE_WITH_SKU",
        url: "#product-LINK",
      },
      {
        name: "Cassetto estraibile scorrevole",
        badge: "Massima capienza",
        price: "€52–68",
        pros: "Scorre come un cassetto vero, ideale per detersivi e panni. Facile da installare.",
        cons: "Serve almeno 40 cm di profondità libera. Non aggira sifoni molto sporgenti.",
        sku: "REPLACE_WITH_SKU",
        url: "#product-LINK",
      },
      {
        name: "Contenitori impilabili trasparenti (set 4)",
        badge: "Miglior rapporto qualità/prezzo",
        price: "€26–32",
        pros: "Modulari, si vedono i prodotti, si lavano in lavastoviglie.",
        cons: "Meno robusti per detersivi grandi. Meglio per spugne, guanti, panni.",
        sku: "REPLACE_WITH_SKU",
        url: "#product-LINK",
      },
      {
        name: "Barra magnetica per flaconi",
        badge: "Idea salvaspazio",
        price: "€29–38",
        pros: "Sospende i flaconi spray al lato del mobile. Libera il fondo per il secchio.",
        cons: "Funziona solo con flaconi metallici o con adesivo magnetico. Portata 3–4 kg.",
        sku: "REPLACE_WITH_SKU",
        url: "#product-LINK",
      },
    ],
    faq: [
      {
        q: "Quale organizer serve se il sifone è centrale?",
        a: "La mensola telescopica: si adatta ai due lati aggirando il centro. Il cassetto estraibile invece funziona solo se il centro è libero.",
      },
      {
        q: "Sono resistenti all'acqua?",
        a: "I modelli qui selezionati sì, ma consigliamo sempre di verniciarne le zone in contatto con detersivi versati.",
      },
      {
        q: "Serve montaggio a viti?",
        a: "Nessuno di questi quattro modelli. Sono studiati per appartamenti in affitto.",
      },
    ],
  },
  {
    slug: "set-dispensa-trasparente",
    category: "cucina-dispensa",
    title: "I migliori set dispensa trasparente",
    excerpt:
      "Set completi in vetro o PP alimentare per travasare pasta, riso, cereali e farina. Estetica pulita, funzionalità reale.",
    read: "8 min",
    image:
      "https://images.unsplash.com/photo-1617510711388-6f88e078d698?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    meta: {
      title: "Migliori set dispensa trasparente 2025 | Nestly",
      description:
        "Set contenitori dispensa selezionati: vetro, PP alimentare, impilabili, ermetici. Recensioni verificate.",
    },
    intro:
      "Travasare pasta, riso, cereali e farina in contenitori trasparenti non è vanity: è protezione dagli infestanti alimentari, migliore leggibilità della scorta, meno sprechi. Abbiamo scelto quattro set selezionati con cura, tutti con chiusura ermetica e forma quadrata o rettangolare (le forme rotonde sprecano spazio).",
    criteria: [
      "Chiusura ermetica testata (guarnizione siliconica)",
      "Forma impilabile: quadrata o rettangolare",
      "Materiale PP alimentare o vetro con coperchio bambù",
      "Recensioni 4★+ con almeno 1000 valutazioni",
      "Prezzo €35–110 per set completo",
    ],
    products: [
      {
        name: "Set 6 contenitori PP con guarnizione",
        badge: "Migliore in generale",
        price: "€39–48",
        pros: "Sei formati diversi da 0.5 a 2 L, impilabili, coperchio a scatto sicuro.",
        cons: "Non adatti al lavaggio in lavastoviglie a temperatura alta.",
        sku: "REPLACE_WITH_SKU",
        url: "#product-LINK",
      },
      {
        name: "Set 4 vasi vetro con coperchio bambù",
        badge: "Estetica premium",
        price: "€52–68",
        pros: "Vetro spesso, coperchio bambù naturale. Bellissimi a vista.",
        cons: "Prezzo più alto, coperchio non 100% ermetico a lungo termine.",
        sku: "REPLACE_WITH_SKU",
        url: "#product-LINK",
      },
      {
        name: "Set 8 mini-barattoli spezie",
        badge: "Miglior salvaspazio",
        price: "€28–35",
        pros: "Piccoli, quadrati, con etichette adesive incluse. Ideali per spezie sfuse.",
        cons: "Capacità 100–150 ml: solo spezie, non alimenti grandi.",
        sku: "REPLACE_WITH_SKU",
        url: "#product-LINK",
      },
      {
        name: "Set 10 contenitori modulari alti",
        badge: "Massima capienza",
        price: "€68–89",
        pros: "Modello alto, ideale per pasta lunga, biscotti, farine. Base larga stabile.",
        cons: "Impegnativo in altezza: verifica ripiani dispensa.",
        sku: "REPLACE_WITH_SKU",
        url: "#product-LINK",
      },
    ],
    faq: [
      {
        q: "Meglio vetro o plastica alimentare?",
        a: "Vetro se la dispensa è a vista o hai spazio (pesa di più). PP alimentare se cerchi leggerezza e più formati modulari.",
      },
      {
        q: "Servono etichette?",
        a: "Sì, sempre. Nome del prodotto e data di travaso. Etichette adesive removibili o washi tape.",
      },
      {
        q: "Come si puliscono?",
        a: "Acqua tiepida e sapone neutro. Evita lavastoviglie ad alte temperature per la plastica: le guarnizioni si rovinano prima.",
      },
    ],
  },
  {
    slug: "divisori-armadio-spazi-stretti",
    category: "armadio-piccolo",
    title: "Divisori armadio per spazi stretti",
    excerpt:
      "Divisori verticali, cassetti in tessuto, appendiabiti sottili. Le soluzioni testate per armadi da 60 a 100 cm.",
    read: "8 min",
    image:
      "https://images.unsplash.com/photo-1558997519-83ea9252edf8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    meta: {
      title: "Migliori divisori armadio spazi stretti | Nestly",
      description:
        "Selezione Nestly per armadi stretti italiani: divisori verticali, appendiabiti in velluto, cassetti in tessuto.",
    },
    intro:
      "Un armadio stretto non è un problema di spazio: è un problema di divisione. Con quattro accessori si trasforma un armadio da 80 centimetri in un contenitore ordinato per due stagioni. Nessuno dei prodotti qui sotto richiede viti o modifiche permanenti, quindi funzionano anche in affitto.",
    criteria: [
      "Compatibili con armadi larghi 60–100 cm",
      "Nessun montaggio con viti",
      "Materiali che non graffiano il legno interno",
      "Recensioni 4★+ con più di 500 valutazioni",
      "Prezzo per set €25–90",
    ],
    products: [
      {
        name: "Appendiabiti in velluto sottile (set 30)",
        badge: "Essenziale",
        price: "€25–34",
        pros: "Metà spazio degli appendiabiti tradizionali, non fanno scivolare i vestiti, gruccia sottile ma robusta.",
        cons: "Il velluto attira un po' di polvere: passare uno straccio ogni 6 mesi.",
        sku: "REPLACE_WITH_SKU",
        url: "#product-LINK",
      },
      {
        name: "Divisori mensola armadio (set 4)",
        badge: "Migliore per pile di maglioni",
        price: "€29–38",
        pros: "Si agganciano al ripiano senza viti, separano le pile che tendono a crollare.",
        cons: "Compatibili solo con ripiani di spessore 1.5–3 cm.",
        sku: "REPLACE_WITH_SKU",
        url: "#product-LINK",
      },
      {
        name: "Cassetti in tessuto pieghevoli (set 6)",
        badge: "Migliore per calzini, intimo, sciarpe",
        price: "€32–42",
        pros: "Rigidi ma leggeri, si piegano quando non si usano, formati misti.",
        cons: "Non adatti a cassetti già stretti (verifica misure prima).",
        sku: "REPLACE_WITH_SKU",
        url: "#product-LINK",
      },
      {
        name: "Barra secondaria appendiabiti",
        badge: "Raddoppia l'appeso",
        price: "€22–29",
        pros: "Si aggancia alla barra esistente, aggiunge un secondo livello. Ideale per camicie e t-shirt.",
        cons: "Solo per capi corti (giacche corte, camicie, magliette).",
        sku: "REPLACE_WITH_SKU",
        url: "#product-LINK",
      },
    ],
    faq: [
      {
        q: "Servono attrezzi per il montaggio?",
        a: "No. Tutti e quattro i prodotti sono senza viti.",
      },
      {
        q: "Sono compatibili con armadi IKEA Pax?",
        a: "Sì, tutti i prodotti si adattano ai formati standard Pax e simili.",
      },
      {
        q: "Da dove iniziare se il budget è basso?",
        a: "Prima gli appendiabiti in velluto e la barra secondaria. Cambiano più cose insieme.",
      },
    ],
  },
  {
    slug: "lazy-susan-porta-spezie",
    category: "cucina-dispensa",
    title: "Lazy susan e porta-spezie: cosa scegliere",
    excerpt:
      "Il ripiano rotante è il piccolo lusso della dispensa. Ecco cosa scegliere per spezie, olii, condimenti.",
    read: "6 min",
    image:
      "https://images.unsplash.com/photo-1645567454567-901dc409551b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    meta: {
      title: "Migliori Lazy Susan e porta-spezie | Nestly",
      description:
        "Selezione Nestly di ripiani rotanti Lazy Susan e porta-spezie: bambù, acciaio, plastica. Compatibili armadi italiani.",
    },
    intro:
      "Il ripiano rotante Lazy Susan è la piccola invenzione che risolve il ripiano d'angolo o la mensola dispensa dove le cose spariscono nel fondo. Un giro e vedi tutto. Ne abbiamo selezionati quattro con occhio ai materiali (bambù bello ma poroso, acciaio duraturo, PP economico) e alle recensioni verificate.",
    criteria: [
      "Diametro 25–35 cm (compatibile con la maggior parte dei ripiani)",
      "Rotazione fluida a 360°",
      "Bordo rialzato per evitare cadute",
      "Recensioni 4★+ con più di 400 valutazioni",
      "Prezzo €18–65",
    ],
    products: [
      {
        name: "Lazy Susan bambù 30 cm",
        badge: "Miglior estetica",
        price: "€28–34",
        pros: "Bello a vista, resistente, rotazione morbida. Ideale su piano cucina.",
        cons: "Il bambù è poroso: pulire subito eventuali versamenti.",
        sku: "REPLACE_WITH_SKU",
        url: "#product-LINK",
      },
      {
        name: "Lazy Susan due piani in acciaio",
        badge: "Massima capienza",
        price: "€38–48",
        pros: "Due piani sovrapposti, ideale per olii, aceti, salse. Molto duraturo.",
        cons: "Peso maggiore. Ingombro in altezza (verifica dispensa).",
        sku: "REPLACE_WITH_SKU",
        url: "#product-LINK",
      },
      {
        name: "Porta-spezie girevole con 20 barattoli",
        badge: "Kit completo",
        price: "€48–65",
        pros: "20 barattoli in vetro con etichette pre-stampate italiane. Rotazione fluida.",
        cons: "Se preferisci le tue spezie sfuse, valuta il ripiano vuoto.",
        sku: "REPLACE_WITH_SKU",
        url: "#product-LINK",
      },
      {
        name: "Lazy Susan PP alimentare 25 cm",
        badge: "Miglior prezzo",
        price: "€18–24",
        pros: "Economico, leggero, lavabile in lavastoviglie. Ideale per ripiani frigo o dispensa.",
        cons: "Meno bello a vista rispetto al bambù. Bordo basso.",
        sku: "REPLACE_WITH_SKU",
        url: "#product-LINK",
      },
    ],
    faq: [
      {
        q: "Meglio bambù o acciaio?",
        a: "Bambù se resta a vista sul piano cucina. Acciaio se sta chiuso nella dispensa e vuoi due piani.",
      },
      {
        q: "Serve un Lazy Susan grande?",
        a: "Meglio più piccoli e specializzati (uno per spezie, uno per olii) che uno grande caotico.",
      },
      {
        q: "Come si pulisce?",
        a: "Bambù: panno umido, mai immerso. Acciaio e PP: acqua e sapone neutro.",
      },
    ],
  },
].map(withEditorialDefaults);

export const ALL_ARTICLES = [...GUIDES, ...BEST_OF];

/**
 * Canonical route of an article. Product round-ups live under /best-of, editorial
 * how-tos under /guida; both are rendered by the same page component.
 * @param {{slug: string, products?: unknown[]}} article
 * @returns {string}
 */
export function articlePath(article) {
  return article.products ? `/best-of/${article.slug}` : `/guida/${article.slug}`;
}

export function findArticle(slug) {
  return ALL_ARTICLES.find((a) => a.slug === slug);
}

export function articlesByCategory(cat) {
  return ALL_ARTICLES.filter((a) => a.category === cat);
}

export function relatedArticles(slug) {
  const a = findArticle(slug);
  if (!a) return [];
  return ALL_ARTICLES.filter((x) => x.slug !== slug && x.category === a.category).slice(0, 3);
}
